import { AlertTriangle } from "lucide-react";
import type { PolicySyncOutcome } from "@/lib/api";

interface Props {
  outcome: PolicySyncOutcome | null;
  error: string | null;
}

// CSR-1703 / CSR-1746 — a subtle, persistent "data may be outdated" status
// shown on the policy page when the most recent carrier sync attempt did not
// confirm fresh data. The CSR-1703 stale-data signal is kept (persistent, so
// it's still visible whenever the operator lands on the page), but the
// CSR-1746 review de-emphasized it from a full-width destructive Alert to a
// single muted/warning line — the full failure reason now lives on the
// timeline "Policy Sync — Failed" card, with `error` retained here as the
// hover title. Renders nothing on `"succeeded"`, `"no_change"` (CSR-1977 — a
// clean re-check that found nothing to change is healthy, not a warning; the
// header's "Checked … — no changes" line carries it) or `null` (no attempt on
// record yet).
export function PolicySyncBanner({ outcome, error }: Props) {
  if (outcome !== "failed" && outcome !== "not_found") return null;

  const isNotFound = outcome === "not_found";

  return (
    <div
      data-testid="policy-sync-banner"
      className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500"
      title={isNotFound ? undefined : error ?? undefined}
    >
      <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>
        {isNotFound
          ? "Policy not found at the carrier on the last sync — data may be outdated"
          : "Last sync didn't complete — data may be outdated"}
      </span>
    </div>
  );
}
